import { Button, ButtonProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import Modal from 'components/Modal';
import Toast from 'components/Toast';
import APP from 'stores/APP';
import security from 'stores/security';
import { Source } from 'types/funding';
import API from 'utils/API';
import http from 'utils/http';
import URL from 'utils/URL';
import usePlaidLink, { PLAID_SCRIPT_ID } from '../usePlaidLink';
import { bindCardStep1 } from './request';
import { PlaidBankCardItem } from './types';
import WarningModal from './WarningModal';

/**
 * @description
 * 接入Plaid
 * https://plaid.com/docs/link/web/
 */
interface PlaidLinkProps {
    className?: string;
    btnText?: string;
    source: Source;
    token?: string; // plaid token 用于更新银行卡和二次验证
    bankInfo?: PlaidBankCardItem;
    limit?: boolean; // 限制创建BankCard
    hideBtn?: boolean; // 隐藏唤起plaid的按钮
    receivedRedirectUri?: string;
    onExit?: (error?) => void;
    onSuccess?: () => void;
    onClick?: () => void;
    icon?: React.ReactElement;
    btnProps?: ButtonProps;
    errorInBtn?: boolean;
}

const PlaidLink: React.FC<PlaidLinkProps> = ({
    source,
    token,
    receivedRedirectUri,
    className,
    btnText,
    icon,
    hideBtn,
    bankInfo,
    limit,
    onSuccess,
    onExit,
    onClick,
    btnProps,
    errorInBtn
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [publicToken, setPublicToken] = useState(token);
    const { loading: sdkLoading, error: sdkError, open, loadSdk } = usePlaidLink();
    const navigate = useNavigate();

    const redirectUri = useMemo(() => {
        return `${window.location.protocol}//${window.location.host}/transaction/plaid/${source}${
            APP.isTiger ? '/inapp' : ''
        }`;
    }, [source]);

    // 获取plaid link_token
    const getPublicToken = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await http.get(API.TRADE.funding.getLinkToken(), {
                params: {
                    customer_id: security.currentCustomerId,
                    redirect_uri: redirectUri
                }
            });

            setPublicToken(res.data?.public_token);
        } catch (err) {
            setError(err);
        }

        setLoading(false);
    }, [redirectUri]);

    const bindCard = useCallback(
        async public_token => {
            // 如果带有token和bankInfo 表示用户触发刷新卡信息逻辑，plaid验证成功后不需要重新绑卡 银行卡信息会自动更新
            if (token && bankInfo) {
                // 入金直接接入输入金额页面
                if (source === 'deposit') {
                    navigate(`/profile/deposit/ach/fill`, {
                        state: bankInfo
                    });
                } else {
                    // 出金需要重新选择银行卡
                    navigate('/profile/withdraw/ach');
                }

                return;
            }

            Toast.loading(true, __('Loading'));

            try {
                const { data } = await bindCardStep1(public_token);

                // 成功后进入选择银行卡页面
                navigate(`/profile/${source}/ach/choose-bankcard`, {
                    state: {
                        public_token,
                        data,
                        source
                    }
                });
            } catch (error) {
                // 失败后进入统一错误页面，引导用户重新绑定
                navigate(`/profile/${source}/ach/complete`, {
                    state: {
                        error_code: error.error_code,
                        type: 'error',
                        source
                    }
                });
            }

            Toast.loading(false);
        },
        [bankInfo, navigate, source, token]
    );

    const handleSuccess = useCallback(
        public_token => {
            // 监听plaid成功回调，这时plaid会返回public_token,用于触发绑卡
            bindCard(public_token);
            onSuccess?.();
        },
        [bindCard, onSuccess]
    );

    const handleExit = useCallback(
        (error, metadata) => {
            if (error != null) {
                // 拼接错误信息和request_id（提供给plaid用来检查绑卡失败的原因）
                Toast.show(
                    `${error.error_message} ${metadata?.request_id ? `(request_id: ${metadata.request_id})` : ''}`
                );
            }

            onExit?.(error);
        },
        [onExit]
    );

    useEffect(() => {
        // 如果没有传token则重新获取一个新
        if (!token) {
            getPublicToken();
        }
    }, [token, getPublicToken]);

    const openPlaid = useCallback(() => {
        if (limit) {
            Modal.open({
                component: WarningModal,
                wrapClassName: 'plaid-warning-modal-container',
                centered: true,
                keyboard: false
            }).result.catch(() => {});

            return;
        }

        // sdk加载成功并且有link_token,此时可以唤起plaid程序
        if (publicToken && !sdkLoading && !sdkError) {
            open({
                receivedRedirectUri,
                token: publicToken,
                redirect_uri: redirectUri,
                onSuccess: handleSuccess,
                onExit: handleExit
            });
        }
    }, [handleSuccess, handleExit, limit, open, publicToken, receivedRedirectUri, redirectUri, sdkError, sdkLoading]);

    useEffect(() => {
        // 表示sdk已经加载完成
        const scriptFile = document.getElementById(PLAID_SCRIPT_ID);

        if (scriptFile && window.Plaid) {
            if (URL.current(receivedRedirectUri).query.oauth_state_id) {
                openPlaid();
            }
        }
    }, [receivedRedirectUri, openPlaid]);

    if (hideBtn && loading) {
        return <Loading />;
    }

    if (sdkError && !errorInBtn) {
        return (
            <ErrorBox
                error={__('Load Error')}
                onClick={() => {
                    loadSdk();
                }}
            />
        );
    }

    if (error && !errorInBtn) {
        return (
            <ErrorBox
                error={error}
                onClick={() => {
                    getPublicToken();
                }}
            />
        );
    }

    if (hideBtn) {
        return null;
    }

    return (
        <Button
            {...btnProps}
            onClick={() => {
                if (errorInBtn) {
                    if (sdkError) {
                        loadSdk();
                    } else if (error) {
                        getPublicToken();
                    }
                }

                onClick?.();
                openPlaid();
            }}
            disabled={loading}
            className={className}>
            {icon}
            {loading
                ? __('Loading...')
                : errorInBtn && (sdkError || error)
                ? __('请求失败 请重试')
                : btnText || __('Open')}
        </Button>
    );
};

export default PlaidLink;
